/**
 * Pointer input on the map: tap, hold, right-click, drag, pinch, twist, tilt and wheel. Fingers
 * and a pen follow the app's touch rules, in `touch.ts`. With a mouse, a drag with the right or
 * middle button, or with Alt held, orbits the camera instead of panning, and a right-click that
 * does not move still places a banner. On a Mac trackpad in Safari, the rotate gesture turns the
 * world. A finger or a click that comes down while the camera moves by itself only stops it
 * (decision 0021): a tap, a hold or a right-click needs another.
 */

import { HOLD_MS, type TouchAction, TouchInterpreter, type TouchPoint } from "./touch.ts";

export interface PointerHandlers {
  tap(devX: number, devY: number): void;
  hold(devX: number, devY: number): void;
  secondary(devX: number, devY: number): void;
  /**
   * A pointer came down on the world: the camera's own motion stops, a glide after a pan or an
   * eased glide, turn or zoom. Whether it was moving, in which case that is all the pointer does.
   */
  stopMotion(): boolean;
  panStart(): void;
  pan(devDx: number, devDy: number): void;
  panEnd(): void;
  pinch(factor: number, devX: number, devY: number): void;
  /** Two fingers turning around their midpoint, in radians, positive clockwise on screen. */
  twist(radians: number, devX: number, devY: number): void;
  /** Two fingers side by side moving up or down together, in device pixels. */
  tilt(devDy: number): void;
  /** Two fingers became one, which drags on, or a trackpad's gesture ended. */
  pinchEnd(): void;
  /** A mouse drag that orbits: right or middle button, or Alt with the left. Device pixels. */
  orbit(devDx: number, devDy: number): void;
  orbitEnd(): void;
  /** Wheel or trackpad scroll, in pixels; `pinch` is true for a trackpad pinch (ctrl + wheel). */
  wheel(deltaY: number, devX: number, devY: number, pinch: boolean): void;
  activity(): void;
}

const DRAG_PX = 8;

export function attachPointer(
  canvas: HTMLCanvasElement,
  dpr: () => number,
  h: PointerHandlers,
): () => void {
  /** Fingers and a pen, read by the app's rules. */
  const fingers = new TouchInterpreter(1);
  /** The timer that makes a finger held still a hold, and the moment it is set for. */
  let fingerHold: ReturnType<typeof setTimeout> | null = null;
  let fingerHoldAt: number | null = null;
  /** The mouse while a button is down. */
  let mouse: {
    id: number;
    x0: number;
    y0: number;
    lastX: number;
    lastY: number;
    drag: boolean;
    /** A drag with the right or middle button, or with Alt: it orbits instead of panning. */
    orbit: boolean;
    done: boolean;
    button: number;
    downAt: number;
    /** It came down on a world moving by itself, and only stopped it. */
    caught: boolean;
  } | null = null;
  let mouseHold: ReturnType<typeof setTimeout> | null = null;

  const clearMouseHold = () => {
    if (mouseHold !== null) clearTimeout(mouseHold);
    mouseHold = null;
  };

  const point = (e: PointerEvent): TouchPoint => ({ id: e.pointerId, x: e.clientX, y: e.clientY });

  /** Applies what the fingers asked for. */
  const apply = (actions: readonly TouchAction[]) => {
    for (const a of actions) {
      switch (a.kind) {
        case "tap":
          h.tap(a.x, a.y);
          break;
        case "hold":
          h.hold(a.x, a.y);
          break;
        case "panBegan":
          h.panStart();
          break;
        case "pan":
          h.pan(a.dx, a.dy);
          break;
        case "panEnded":
          h.panEnd();
          break;
        case "pinch":
          h.pinch(a.factor, a.x, a.y);
          break;
        case "twist":
          h.twist(a.radians, a.x, a.y);
          break;
        case "tilt":
          h.tilt(a.dy);
          break;
        case "pinchEnded":
          h.pinchEnd();
          break;
      }
    }
  };

  /** Sets the hold's timer for the moment the fingers wait for, or none. */
  const followHold = () => {
    const at = fingers.holdAt;
    if (at === fingerHoldAt) return;
    if (fingerHold !== null) clearTimeout(fingerHold);
    fingerHold = null;
    fingerHoldAt = at;
    if (at === null) return;
    fingerHold = setTimeout(
      () => {
        fingerHold = null;
        fingerHoldAt = null;
        apply(fingers.holdDue());
        followHold();
      },
      Math.max(0, at - performance.now()),
    );
  };

  const mouseDown = (e: PointerEvent) => {
    if (mouse) return;
    mouse = {
      id: e.pointerId,
      x0: e.clientX,
      y0: e.clientY,
      lastX: e.clientX,
      lastY: e.clientY,
      drag: false,
      orbit: e.button === 1 || e.button === 2 || e.altKey,
      done: false,
      button: e.button,
      downAt: performance.now(),
      caught: h.stopMotion(),
    };
    // The middle button would start the browser's autoscroll.
    if (e.button === 1) e.preventDefault();
    if (e.button === 0 && !e.altKey && !mouse.caught) {
      mouseHold = setTimeout(() => {
        if (mouse && !mouse.drag) {
          mouse.done = true;
          h.hold(mouse.x0 * dpr(), mouse.y0 * dpr());
        }
      }, HOLD_MS);
    }
  };

  const mouseMove = (e: PointerEvent) => {
    if (!mouse || e.pointerId !== mouse.id) return;
    if (!mouse.drag && Math.hypot(e.clientX - mouse.x0, e.clientY - mouse.y0) > DRAG_PX) {
      mouse.drag = true;
      clearMouseHold();
      if (!mouse.orbit) h.panStart();
    }
    if (mouse.drag) {
      const dx = (e.clientX - mouse.lastX) * dpr();
      const dy = (e.clientY - mouse.lastY) * dpr();
      if (mouse.orbit) h.orbit(dx, dy);
      else h.pan(dx, dy);
    }
    mouse.lastX = e.clientX;
    mouse.lastY = e.clientY;
  };

  const mouseUp = (e: PointerEvent, cancelled: boolean) => {
    if (!mouse || e.pointerId !== mouse.id) return;
    clearMouseHold();
    if (mouse.drag) {
      if (mouse.orbit) h.orbitEnd();
      else h.panEnd();
    } else if (cancelled || mouse.button === 1 || mouse.done || mouse.caught) {
      // A middle click does nothing on the map, a hold has acted, and a click that stopped the
      // world did only that.
    } else {
      const x = e.clientX * dpr();
      const y = e.clientY * dpr();
      // A hold whose timer was delayed by a busy main thread still counts as a hold.
      const held = mouse.button !== 2 && performance.now() - mouse.downAt >= HOLD_MS;
      if (mouse.button === 2) h.secondary(x, y);
      else if (held) h.hold(mouse.x0 * dpr(), mouse.y0 * dpr());
      else h.tap(x, y);
    }
    mouse = null;
  };

  const onDown = (e: PointerEvent) => {
    h.activity();
    try {
      canvas.setPointerCapture(e.pointerId);
    } catch {
      // Capture is best effort.
    }
    if (e.pointerType === "mouse") {
      mouseDown(e);
      return;
    }
    fingers.pixelsPerPoint = dpr();
    // The first finger down catches the world; if it was moving, that is all the touch does.
    const catching = !fingers.touching && h.stopMotion();
    apply(fingers.began(point(e), performance.now(), catching));
    followHold();
  };

  const onMove = (e: PointerEvent) => {
    if (e.pointerType === "mouse") {
      mouseMove(e);
      return;
    }
    apply(fingers.moved([point(e)]));
    followHold();
  };

  const onUp = (e: PointerEvent) => {
    if (e.pointerType === "mouse") {
      mouseUp(e, false);
      return;
    }
    apply(fingers.ended(point(e), performance.now()));
    followHold();
  };

  const onCancel = (e: PointerEvent) => {
    if (e.pointerType === "mouse") {
      mouseUp(e, true);
      return;
    }
    apply(fingers.cancelled(e.pointerId, performance.now()));
    followHold();
  };

  const onContextMenu = (e: Event) => e.preventDefault();
  // Safari's gestures, for a Mac's trackpad. Its pinch also arrives there as a wheel with ctrl, so
  // wheel pinches are ignored while a gesture runs, or the zoom would count twice. On an iPhone or
  // an iPad, Safari sends them for two fingers too, which the pointer events already follow:
  // counted as well, every turn and zoom went twice as far as the fingers (decision 0021).
  let gestureActive = false;
  let gestureByFingers = false;
  let gestureRotation = 0;
  let gestureScale = 1;
  const onGestureStart = (e: Event) => {
    e.preventDefault();
    gestureActive = true;
    gestureByFingers = fingers.touching;
    gestureRotation = 0;
    gestureScale = 1;
  };
  const onGestureChange = (e: Event) => {
    e.preventDefault();
    if (gestureByFingers || fingers.touching) {
      gestureByFingers = true;
      return;
    }
    const g = e as Event & {
      rotation?: number;
      scale?: number;
      clientX?: number;
      clientY?: number;
    };
    const x = (g.clientX ?? innerWidth / 2) * dpr();
    const y = (g.clientY ?? innerHeight / 2) * dpr();
    const rotation = g.rotation ?? 0;
    const scale = g.scale ?? 1;
    if (rotation !== gestureRotation) h.twist(((rotation - gestureRotation) * Math.PI) / 180, x, y);
    if (scale > 0 && gestureScale > 0 && scale !== gestureScale)
      h.pinch(scale / gestureScale, x, y);
    gestureRotation = rotation;
    gestureScale = scale;
  };
  const onGestureEnd = (e: Event) => {
    e.preventDefault();
    gestureActive = false;
    if (!gestureByFingers) h.pinchEnd();
    gestureByFingers = false;
  };
  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    if (gestureActive && e.ctrlKey) return;
    // Lines and pages to pixels, roughly, so a mouse wheel and a trackpad scroll alike.
    const unit = e.deltaMode === 1 ? 40 : e.deltaMode === 2 ? 800 : 1;
    h.wheel(e.deltaY * unit, e.clientX * dpr(), e.clientY * dpr(), e.ctrlKey);
  };

  canvas.addEventListener("pointerdown", onDown);
  canvas.addEventListener("pointermove", onMove);
  canvas.addEventListener("pointerup", onUp);
  canvas.addEventListener("pointercancel", onCancel);
  canvas.addEventListener("contextmenu", onContextMenu);
  canvas.addEventListener("wheel", onWheel, { passive: false });
  canvas.addEventListener("gesturestart", onGestureStart);
  canvas.addEventListener("gesturechange", onGestureChange);
  canvas.addEventListener("gestureend", onGestureEnd);
  return () => {
    clearMouseHold();
    if (fingerHold !== null) clearTimeout(fingerHold);
    canvas.removeEventListener("pointerdown", onDown);
    canvas.removeEventListener("pointermove", onMove);
    canvas.removeEventListener("pointerup", onUp);
    canvas.removeEventListener("pointercancel", onCancel);
    canvas.removeEventListener("contextmenu", onContextMenu);
    canvas.removeEventListener("wheel", onWheel);
    canvas.removeEventListener("gesturestart", onGestureStart);
    canvas.removeEventListener("gesturechange", onGestureChange);
    canvas.removeEventListener("gestureend", onGestureEnd);
  };
}
